import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Camera, Image as ImageIcon, Check, RotateCw, ArrowLeft, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { compressImage } from "@/lib/imageCompress";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND}/api`;

/**
 * Mobile-first photo capture flow for field workers.
 *
 * Works in TWO modes:
 *   - Authenticated: pass `report` (with report_id). Uses /api/reports/{report_id}/...
 *   - Edit-token (installer link): pass `editToken`. Uses /api/reports/edit/{editToken}/...
 *
 * Flow:
 *   1. Source picker (Kamera / Galleria) — auto-triggers Kamera on mount
 *   2. After capture: preview + caption input + "Vaatii jatkotoimenpiteitä"
 *      checkbox. If on, caption is REQUIRED and is appended to the unit's
 *      additional_work_text (photo also tagged is_finding=true).
 *   3. After save: success screen with "Ota seuraava kuva" / "Takaisin yksikköön".
 */
export default function PhotoCaptureSheet({ report, unit, units, editToken, autoCamera = false, autoGallery = false, onClose, onPhotoSaved }) {
  const isEditMode = !!editToken;
  const [targetUnit, setTargetUnit] = useState(unit || null);
  // `units` prop is required when `unit` is null. If there's only one available
  // unit we auto-resolve to it; otherwise we drop into a unit-pick phase AFTER
  // the photo is captured (per user UX request).
  const candidateUnits = units || (report?.units || []);
  const photoUrlFor = (u) => isEditMode
    ? `${API_BASE}/reports/edit/${editToken}/units/${u.id}/photos`
    : `/reports/${report.report_id}/units/${u.id}/photos`;
  const unitPatchUrlFor = (u) => isEditMode
    ? `${API_BASE}/reports/edit/${editToken}/units/${u.id}`
    : `/reports/${report.report_id}/units/${u.id}`;
  const post = (url, body, config) => (isEditMode ? axios.post(url, body, config) : api.post(url, body, config));
  const patch = (url, body) => (isEditMode ? axios.patch(url, body) : api.patch(url, body));

  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const captionRef = useRef(null);
  // Ref holds the in-flight compression promise. We kick it off the moment a
  // file is selected, so by the time the user finishes typing a caption and
  // hits Tallenna, the work is usually already done. Race-cancellation is
  // handled by reset()/retake() which clears the ref.
  const compressPromiseRef = useRef(null);
  // phase: 'opening' = autoCamera/autoGallery waiting for OS picker, 'picker' = manual choice,
  // 'review' = caption + unit + follow-up, 'saving' / 'success'.
  const [phase, setPhase] = useState((autoCamera || autoGallery) ? "opening" : "picker");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [caption, setCaption] = useState("");
  const [needsFollowUp, setNeedsFollowUp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // When `autoCamera` (or `autoGallery`) is true, fire the appropriate file
  // input immediately on mount. We render a clean "Avataan kameraa…" / "Avataan
  // galleriaa…" loader so the user never sees a flash of UI before the OS
  // takes over.
  useEffect(() => {
    if (!autoCamera && !autoGallery) return;
    const input = (autoCamera ? cameraRef.current : galleryRef.current);
    let cancelled = false;
    const fireClick = () => {
      if (!cancelled && input) input.click();
    };
    const raf = requestAnimationFrame(fireClick);
    const onCancel = () => {
      if (!cancelled) setPhase("picker");
    };
    input?.addEventListener("cancel", onCancel);
    const fallback = setTimeout(() => {
      setPhase((p) => (p === "opening" ? "picker" : p));
    }, 1800);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      clearTimeout(fallback);
      input?.removeEventListener("cancel", onCancel);
    };
  }, [autoCamera, autoGallery]);

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      setFile(f);
      // Kick off compression IMMEDIATELY in the background — we don't await
      // here. By the time the user finishes typing the caption and hits
      // Tallenna, the resulting File is usually already cached in the ref.
      compressPromiseRef.current = compressImage(f).catch((err) => {
        // Store the error so save() can fall back to the raw file gracefully.
        // eslint-disable-next-line no-console
        console.warn("Background compression failed, will retry at save:", err);
        return null;
      });
      setCaption("");
      setNeedsFollowUp(false);
      setError("");
      // Auto-resolve to single unit when applicable.
      if (!targetUnit && candidateUnits.length === 1) {
        setTargetUnit(candidateUnits[0]);
      }
      // Always go to review — unit selector is shown inline if still missing.
      setPhase("review");
      setTimeout(() => captionRef.current?.focus(), 150);
    }
    e.target.value = ""; // allow re-selecting the same file
  };

  // Keep targetUnit so "Ota seuraava kuva" continues for the same unit.
  // If the user wants a different unit, they close the sheet and reopen the FAB.
  const reset = () => {
    setFile(null);
    setPreviewUrl(null);
    setCaption("");
    setNeedsFollowUp(false);
    setError("");
    compressPromiseRef.current = null;
    setPhase("picker");
  };

  const retake = () => {
    reset();
    // Do NOT auto-click camera — let the user pick "Avaa kamera" or "Galleria"
    // again, mirroring the same UX they got the first time.
  };

  const save = async () => {
    const trimmed = caption.trim();
    if (!targetUnit) {
      setError("Valitse yksikkö ensin.");
      return;
    }
    if (needsFollowUp && !trimmed) {
      setError("Lisää lyhyt kuvaus havainnosta.");
      captionRef.current?.focus();
      return;
    }
    setBusy(true);
    setPhase("saving");
    setError("");
    try {
      // Re-use the in-flight pre-compression kicked off in handleFile.
      // Most often this Promise is already resolved (user spent N seconds
      // typing the caption while compression ran in the worker).
      let compressed = compressPromiseRef.current ? await compressPromiseRef.current : null;
      if (!compressed) {
        // Pre-compress failed or wasn't fired — do it inline as a fallback.
        compressed = await compressImage(file);
      }
      const fd = new FormData();
      fd.append("file", compressed, file.name || "photo.jpg");
      fd.append("caption", trimmed);
      fd.append("is_finding", needsFollowUp ? "true" : "false");
      const { data } = await post(
        photoUrlFor(targetUnit),
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );

      // If "needs follow-up" was checked, append caption to additional_work_text
      let nextReport = data?.report;
      if (needsFollowUp && trimmed && nextReport) {
        const u = (nextReport.units || []).find((x) => x.id === targetUnit.id);
        const current = (u?.additional_work_text || "").trim();
        const newText = current ? `${current}\n${trimmed}` : trimmed;
        try {
          const { data: patched } = await patch(unitPatchUrlFor(targetUnit), { additional_work_text: newText });
          if (patched) nextReport = patched;
        } catch {
          // best-effort: keep the report-with-photo even if the append fails
        }
      }

      onPhotoSaved?.(nextReport);
      setPhase("success");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tallennus epäonnistui");
      setPhase("review");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-end md:items-center justify-center" data-testid="photo-capture-sheet">
      {/* Hidden file inputs */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFile}
        data-testid="photo-capture-camera-input"
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
        data-testid="photo-capture-gallery-input"
      />

      <div className="bg-white w-full md:max-w-md md:rounded-2xl rounded-t-2xl shadow-2xl max-h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-emerald-600">Lisää kuva</div>
            <div className="font-display text-base font-semibold text-slate-900 truncate">
              {targetUnit ? targetUnit.label : "Valitse yksikkö kuvan oton jälkeen"}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="photo-capture-close">
            <X className="h-5 w-5 text-slate-400" />
          </button>
        </div>

        {/* PHASE: opening — clean loader while the OS picker is being launched.
            User never sees Kamera/Galleria buttons during autoCamera/autoGallery. */}
        {phase === "opening" && (
          <div className="p-10 flex flex-col items-center justify-center gap-3" data-testid="photo-capture-opening">
            <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center animate-pulse">
              <Camera className="h-6 w-6 text-emerald-700" />
            </div>
            <div className="text-sm font-semibold text-slate-700">
              {autoGallery ? "Avataan galleriaa…" : "Avataan kameraa…"}
            </div>
          </div>
        )}

        {/* PHASE: picker (initial) — Kamera / Galleria choice (no auto-fire) */}
        {phase === "picker" && (
          <div className="p-6 flex flex-col gap-3">
            <button
              type="button"
              onClick={() => cameraRef.current?.click()}
              className="flex items-center justify-center gap-2.5 px-4 py-4 rounded-xl bg-emerald-600 text-white font-semibold text-base hover:bg-emerald-700"
              data-testid="photo-capture-open-camera"
            >
              <Camera className="h-5 w-5" /> Avaa kamera
            </button>
            <button
              type="button"
              onClick={() => galleryRef.current?.click()}
              className="flex items-center justify-center gap-2.5 px-4 py-4 rounded-xl bg-white border-2 border-slate-200 text-slate-700 font-semibold text-base hover:bg-slate-50"
              data-testid="photo-capture-open-gallery"
            >
              <ImageIcon className="h-5 w-5" /> Galleria
            </button>
          </div>
        )}

        {/* PHASE: review — inline unit-pick + caption + follow-up + save */}
        {phase === "review" && previewUrl && (
          <>
            <div className="bg-slate-900 flex items-center justify-center" style={{ maxHeight: "40vh" }}>
              <img
                src={previewUrl}
                alt="esikatselu"
                className="max-h-[40vh] w-auto object-contain"
                data-testid="photo-capture-preview"
              />
            </div>
            <div className="p-4 space-y-3 flex-1 overflow-y-auto">
              {/* Inline unit selector — shown only when no target unit set yet
                  OR when there are multiple candidates and user might want to switch */}
              {!unit && candidateUnits.length > 1 && (
                <div data-testid="photo-capture-unit-inline">
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Yksikkö</label>
                  <div className="relative">
                    <select
                      value={targetUnit?.id || ""}
                      onChange={(e) => {
                        const u = candidateUnits.find((x) => x.id === e.target.value);
                        setTargetUnit(u || null);
                        setError("");
                      }}
                      className="input-base appearance-none pr-9"
                      data-testid="photo-capture-unit-select"
                    >
                      <option value="">Valitse yksikkö…</option>
                      {candidateUnits.map((u) => (
                        <option key={u.id} value={u.id} data-testid={`photo-capture-unit-option-${u.id}`}>
                          {u.label}{u.status === "done" ? " ✓" : u.status === "inaccessible" ? " ✕" : ""}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Kuvateksti</label>
                <textarea
                  ref={captionRef}
                  value={caption}
                  onChange={(e) => { setCaption(e.target.value); setError(""); }}
                  rows={2}
                  placeholder="Esim. 'Venttiilit puhdistettu' tai 'Ohjauskortissa palojälkiä'"
                  className={`input-base resize-none ${error ? "border-rose-400 ring-1 ring-rose-200" : ""}`}
                  data-testid="photo-capture-caption"
                />
                {error && <div className="mt-1 text-xs text-rose-600 font-medium" data-testid="photo-capture-error">{error}</div>}
              </div>

              <label className="flex items-start gap-2.5 p-3 rounded-xl border border-amber-200 bg-amber-50/40 cursor-pointer" data-testid="photo-capture-follow-up-label">
                <input
                  type="checkbox"
                  checked={needsFollowUp}
                  onChange={(e) => setNeedsFollowUp(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-amber-600"
                  data-testid="photo-capture-follow-up"
                />
                <div className="flex-1 text-sm">
                  <div className="font-semibold text-slate-800">Vaatii jatkotoimenpiteitä</div>
                  <div className="text-xs text-slate-500 mt-0.5">Kuvateksti lisätään automaattisesti yksikön &quot;Havainnot / jatkotoimenpiteet&quot; -osioon.</div>
                </div>
              </label>
            </div>
            <div className="p-4 border-t border-slate-100 flex gap-2">
              <button
                type="button"
                onClick={retake}
                disabled={busy}
                className="px-3 py-3 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1.5"
                data-testid="photo-capture-retake"
              >
                <RotateCw className="h-4 w-4" /> Ota uusi
              </button>
              <button
                type="button"
                onClick={save}
                disabled={busy}
                className="flex-1 px-4 py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50"
                data-testid="photo-capture-save"
              >
                Tallenna
              </button>
            </div>
          </>
        )}

        {/* PHASE: saving */}
        {phase === "saving" && (
          <div className="p-10 text-center">
            <div className="inline-block h-10 w-10 rounded-full border-4 border-emerald-600 border-t-transparent animate-spin" />
            <div className="mt-4 font-display text-base font-semibold text-slate-900">Tallennetaan kuvaa...</div>
          </div>
        )}

        {/* PHASE: success — quick next-action choice */}
        {phase === "success" && (
          <div className="p-6 text-center" data-testid="photo-capture-success">
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <Check className="h-8 w-8 text-emerald-600" strokeWidth={3} />
            </div>
            <div className="mt-3 font-display text-lg font-semibold text-slate-900">
              Kuva lisätty <span className="text-emerald-700">{targetUnit?.label}</span>
            </div>
            {needsFollowUp && (
              <div className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
                Lisätty havaintoihin
              </div>
            )}
            <div className="mt-6 flex flex-col gap-2">
              <button
                type="button"
                onClick={retake}
                className="px-4 py-3 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700"
                data-testid="photo-capture-take-next"
              >
                <Camera className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
                Ota seuraava kuva
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-3 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50"
                data-testid="photo-capture-back-to-unit"
              >
                <ArrowLeft className="h-4 w-4 inline-block mr-1.5 -mt-0.5" />
                Takaisin yksikköön
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
